import { useCopyToClipboard } from '@/hooks/useCopyToClipboard';
import { isProductFeedbackAvailable } from '@/utils/heidiReach';
import { Button } from '@heidi/design-system/button';
import { ThumbsDownFilled, ThumbsUpFilled } from '@heidi/design-system/feedback-thumbs';
import {
  Tooltip,
  TooltipArrow,
  TooltipContent,
  TooltipTrigger,
} from '@heidi/design-system/tooltip';
import { Check, Copy, Info, ListEnd, ThumbsDown, ThumbsUp, Undo2, X } from 'lucide-react';
import { useState } from 'react';
import { useIntl } from 'react-intl';
import { inlineEditBarMessages as messages } from './inline-edit-bar.messages';

export type InlineFeedbackType = 'positive' | 'negative';

const IconOnlyAction = ({
  onClick,
  label,
  testId,
  className,
  children,
}: {
  onClick?: () => void;
  label: string;
  testId: string;
  className?: string;
  children: React.ReactNode;
}) => (
  <Tooltip>
    <TooltipTrigger asChild>
      <Button
        type="button"
        variant="ghost"
        size="sm"
        iconOnly
        onClick={onClick}
        aria-label={label}
        data-testid={testId}
        className={className}
      >
        {children}
      </Button>
    </TooltipTrigger>
    <TooltipContent side="top">
      <TooltipArrow />
      {label}
    </TooltipContent>
  </Tooltip>
);

/** Copies the inline response; the icon flips to a check while copied. */
const CopyAction = ({ text }: { text: string }) => {
  const intl = useIntl();
  const { isCopied, copyToClipboard } = useCopyToClipboard();

  return (
    <IconOnlyAction
      onClick={() => void copyToClipboard(text)}
      label={intl.formatMessage(messages.copy)}
      testId="inline-edit-copy"
    >
      {isCopied ? <Check className="text-validation-success" /> : <Copy />}
    </IconOnlyAction>
  );
};

/**
 * Thumbs up / thumbs down pair on the left of the review rows: captures
 * feedback on the inline response. The chosen thumb fills; feedback can be
 * switched but not un-set (matching the Evidence chat feedback behaviour).
 */
export const InlineFeedbackActions = ({
  onFeedback,
}: {
  onFeedback?: (feedback: InlineFeedbackType) => void;
}) => {
  const intl = useIntl();
  const [selected, setSelected] = useState<InlineFeedbackType | null>(null);

  const select = (feedback: InlineFeedbackType) => {
    if (selected === feedback) return;
    setSelected(feedback);
    onFeedback?.(feedback);
  };

  // An empty slot rather than nothing: the row spaces its two groups apart,
  // and the actions on the right must stay on the right.
  if (!isProductFeedbackAvailable) return <div aria-hidden />;

  return (
    <div className="flex shrink-0 items-center gap-0.5">
      <IconOnlyAction
        onClick={() => select('positive')}
        label={intl.formatMessage(messages.goodResponse)}
        testId="inline-edit-feedback-positive"
        className="text-text-secondary"
      >
        {selected === 'positive' ? <ThumbsUpFilled /> : <ThumbsUp />}
      </IconOnlyAction>
      <IconOnlyAction
        onClick={() => select('negative')}
        label={intl.formatMessage(messages.badResponse)}
        testId="inline-edit-feedback-negative"
        className="text-text-secondary"
      >
        {selected === 'negative' ? <ThumbsDownFilled /> : <ThumbsDown />}
      </IconOnlyAction>
    </div>
  );
};

/** The inline response couldn't answer: no Accept — offer the chat hand-off. */
export const UnansweredReviewRow = ({
  onDiscard,
  onAskInChat,
}: {
  onDiscard: () => void;
  onAskInChat?: () => void;
}) => {
  const intl = useIntl();
  return (
    <>
      <div className="flex min-w-0 items-center gap-2">
        <Info size={14} className="shrink-0 text-text-secondary" />
        <span className="truncate text-sm text-text-primary">
          {intl.formatMessage(messages.couldNotAnswer)}
        </span>
      </div>
      <div className="flex shrink-0 items-center gap-1">
        <IconOnlyAction
          onClick={onDiscard}
          label={intl.formatMessage(messages.dismiss)}
          testId="inline-edit-dismiss"
        >
          <X />
        </IconOnlyAction>
        <Button
          type="button"
          variant="default"
          size="sm"
          onClick={onAskInChat}
          data-testid="inline-edit-ask-in-chat"
        >
          {intl.formatMessage(messages.askInChat)}
        </Button>
      </div>
    </>
  );
};

export const InsightReviewRow = ({
  copyText,
  onDiscard,
  onViewInTranscript,
  onFeedback,
}: {
  copyText?: string | null;
  onDiscard: () => void;
  onViewInTranscript?: () => void;
  onFeedback?: (feedback: InlineFeedbackType) => void;
}) => {
  const intl = useIntl();
  return (
    <>
      <InlineFeedbackActions onFeedback={onFeedback} />
      <div className="flex shrink-0 items-center gap-1">
        {copyText && <CopyAction text={copyText} />}
        {onViewInTranscript ? (
          <Button
            type="button"
            variant="outline"
            size="sm"
            onClick={onViewInTranscript}
            data-testid="inline-edit-view-in-transcript"
          >
            {intl.formatMessage(messages.viewInTranscript)}
          </Button>
        ) : null}
        <Button
          type="button"
          variant="default"
          size="sm"
          onClick={onDiscard}
          data-testid="inline-edit-close"
        >
          {intl.formatMessage(messages.close)}
        </Button>
      </div>
    </>
  );
};

export const EditReviewRow = ({
  copyText,
  onAccept,
  onInsertBelow,
  onReprompt,
  onFeedback,
}: {
  copyText?: string | null;
  onAccept: () => void;
  onInsertBelow: () => void;
  /** Drops the proposal and reopens the prompt input to re-run the edit. */
  onReprompt: () => void;
  onFeedback?: (feedback: InlineFeedbackType) => void;
}) => {
  const intl = useIntl();
  return (
    <>
      <InlineFeedbackActions onFeedback={onFeedback} />
      <div className="flex shrink-0 items-center gap-1">
        {copyText && <CopyAction text={copyText} />}
        <IconOnlyAction
          onClick={onInsertBelow}
          label={intl.formatMessage(messages.insertBelow)}
          testId="inline-edit-insert-below"
        >
          <ListEnd />
        </IconOnlyAction>
        <IconOnlyAction
          onClick={onReprompt}
          label={intl.formatMessage(messages.undo)}
          testId="inline-edit-undo"
        >
          <Undo2 />
        </IconOnlyAction>
        <Button
          type="button"
          variant="default"
          size="sm"
          onClick={onAccept}
          data-testid="inline-edit-accept"
        >
          {intl.formatMessage(messages.accept)}
        </Button>
      </div>
    </>
  );
};
